import React, { createContext, useContext, ReactNode } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';

// The publishable key is fetched from the server (never hardcoded in the client).
// The promise is memoized so the config endpoint is hit at most once per session;
// on failure the memo is cleared so a later call can retry.
let publishableKeyPromise: Promise<string> | null = null;

export const getPublishableKey = (): Promise<string> => {
  if (!publishableKeyPromise) {
    publishableKeyPromise = (async () => {
      const response = await fetch('/api/stripe/config');
      if (!response.ok) {
        throw new Error(`Failed to fetch Stripe config: HTTP ${response.status}`);
      }
      const data = await response.json();
      if (!data.success || !data.publishable_key) {
        throw new Error(data.error || 'Stripe config response missing publishable key');
      }
      return data.publishable_key as string;
    })().catch((error) => {
      publishableKeyPromise = null;
      throw error;
    });
  }
  return publishableKeyPromise;
};

const stripePromise: Promise<Stripe | null> = getPublishableKey()
  .then((key) => loadStripe(key))
  .catch((error) => {
    console.error('Failed to initialize Stripe:', error);
    return null;
  });

// Helper to create a Stripe instance bound to a connected account
export const getStripeForAccount = (connectedAccountId: string): Promise<Stripe | null> => {
  return getPublishableKey().then((key) =>
    loadStripe(key, { stripeAccount: connectedAccountId })
  );
};

interface StripeContextType {
  stripe: Promise<Stripe | null>;
}

const StripeContext = createContext<StripeContextType | null>(null);

export const useStripeContext = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripeContext must be used within StripeProvider');
  }
  return context;
};

interface StripeProviderProps {
  children: ReactNode;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  return (
    <StripeContext.Provider value={{ stripe: stripePromise }}>
      {children}
    </StripeContext.Provider>
  );
};
